import { TaskAdmin } from '@gleamer/types';

type AdminLabelTask = TaskAdmin.AdminLabelTask;

/**
 * What the tasks page derives from the list, and how it searches, filters, sorts and groups it — all in the
 * browser, on the whole list (useAdminTasks). Nothing here is stored: a task has no product or status of
 * its own yet, so the page shows what the list implies.
 */

export type TaskStatus =
  | 'active'
  | 'disabled'
  | 'computing'
  | 'compute-error'
  | 'importing'
  | 'import-error';

export const TASK_STATUSES: { value: TaskStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'computing', label: 'Computing items' },
  { value: 'compute-error', label: 'Items failed' },
  { value: 'importing', label: 'Predictions importing' },
  { value: 'import-error', label: 'Predictions failed' },
  { value: 'disabled', label: 'Disabled' },
];

/**
 * Disabled first (it hides the task from everyone), then the state of its items (labellers are held while they
 * compute), then the state of its predictions. A list without `computationState` (an older viewer-api) reads as
 * READY.
 */
export function taskStatus(task: AdminLabelTask): TaskStatus {
  if (task.disabled) {
    return 'disabled';
  }
  if (task.computationState === 'COMPUTING') {
    return 'computing';
  }
  if (task.computationState === 'COMPUTE_ERROR') {
    return 'compute-error';
  }
  if (task.predictionState === 'IMPORTING') {
    return 'importing';
  }
  if (task.predictionState === 'IMPORT_ERROR') {
    return 'import-error';
  }
  return 'active';
}

export function statusLabel(status: TaskStatus): string {
  return TASK_STATUSES.find(s => s.value === status)?.label ?? status;
}

/** The issues the latest computation of the task's items recorded; 0 when the list does not say. */
export function computationIssues(task: AdminLabelTask): number {
  return task.computation?.issues ?? 0;
}

/**
 * The first word of the name: the data managers name tasks by product and study (NODULE_…, LCT_…, BL_…,
 * ICH_…, PE_…), so it stands in for the product the platform does not record.
 */
export function namePrefix(task: Pick<AdminLabelTask, 'name'>): string {
  const word = task.name.trim().split(/[\s_:-]+/)[0];
  return word ? word.toUpperCase() : '—';
}

/** `project.dataset.table` → its parts; a name without dots is all table. */
export function splitTable(table: string | null | undefined): {
  location: string;
  table: string;
} {
  if (!table) {
    return { location: '', table: '' };
  }
  const at = table.lastIndexOf('.');
  return at < 0
    ? { location: '', table }
    : { location: table.slice(0, at), table: table.slice(at + 1) };
}

export function reviewersOf(task: AdminLabelTask): string[] {
  return task.reviewConfiguration?.reviewers ?? [];
}

/** The taskers who label: viewer-api counts the reviewers among the taskers. */
export function labellersOf(task: AdminLabelTask): string[] {
  const reviewers = reviewersOf(task);
  return (task.taskers ?? []).filter(email => !reviewers.includes(email));
}

/** Does the list carry the taskers (gdata-backend #849)? Without them the labeller views are empty. */
export function listHasTaskers(tasks: AdminLabelTask[]): boolean {
  return tasks.some(task => Array.isArray(task.taskers));
}

export const NO_LABELLER = 'No labeller';
export const NO_REVIEWER = 'No reviewer';

export type Feature = 'iteration' | 'predictions' | 'review';

export const FEATURES: { value: Feature; label: string }[] = [
  { value: 'iteration', label: 'Starts from earlier passes' },
  { value: 'predictions', label: 'Shows predictions' },
  { value: 'review', label: 'Has reviewers' },
];

export function hasFeature(task: AdminLabelTask, feature: Feature): boolean {
  switch (feature) {
    case 'iteration':
      return !!task.bigQueryInitialPassesTableId;
    case 'predictions':
      return task.predictionImportId != null;
    case 'review':
      return reviewersOf(task).length > 0;
  }
}

export type Sort =
  | 'newest'
  | 'oldest'
  | 'name'
  | 'name-desc'
  | 'id'
  | 'revisions';

export const SORTS: Record<
  Sort,
  { label: string; compare: (a: AdminLabelTask, b: AdminLabelTask) => number }
> = {
  newest: {
    label: 'Newest first',
    compare: (a, b) => b.createdAt.localeCompare(a.createdAt),
  },
  oldest: {
    label: 'Oldest first',
    compare: (a, b) => a.createdAt.localeCompare(b.createdAt),
  },
  name: { label: 'Name A–Z', compare: (a, b) => a.name.localeCompare(b.name) },
  'name-desc': {
    label: 'Name Z–A',
    compare: (a, b) => b.name.localeCompare(a.name),
  },
  id: { label: 'Task id', compare: (a, b) => a.taskId - b.taskId },
  revisions: {
    label: 'Most revised',
    compare: (a, b) =>
      b.revision - a.revision || b.createdAt.localeCompare(a.createdAt),
  },
};

export type Grouping =
  | 'none'
  | 'prefix'
  | 'status'
  | 'labeller'
  | 'reviewer'
  | 'creator'
  | 'dataset'
  | 'month';

/** Each grouping gives a task its groups: one, except people — a task is under each of its labellers. */
export const GROUPINGS: Record<
  Grouping,
  { label: string; keys: (task: AdminLabelTask) => string[] }
> = {
  none: { label: 'No grouping', keys: () => [''] },
  prefix: { label: 'Name prefix', keys: task => [namePrefix(task)] },
  status: {
    label: 'Status',
    keys: task => [statusLabel(taskStatus(task))],
  },
  labeller: {
    label: 'Labeller',
    keys: task => {
      const labellers = labellersOf(task);
      return labellers.length ? labellers : [NO_LABELLER];
    },
  },
  reviewer: {
    label: 'Reviewer',
    keys: task => {
      const reviewers = reviewersOf(task);
      return reviewers.length ? reviewers : [NO_REVIEWER];
    },
  },
  creator: { label: 'Created by', keys: task => [task.createdBy || '—'] },
  dataset: {
    label: 'BigQuery dataset',
    keys: task => [splitTable(task.dataset).location || '—'],
  },
  month: {
    label: 'Month created',
    keys: task => [task.createdAt.slice(0, 7)],
  },
};

export type TaskQuery = {
  search: string;
  status: TaskStatus | 'all';
  feature: Feature | 'any';
  creator: string;
  prefix: string;
  /** A labeller's or a reviewer's e-mail: the tasks they are on. */
  person: string;
  sort: Sort;
};

/** The words of the search, each found somewhere in the task: id, name, description, tables, people. */
export function matches(task: AdminLabelTask, words: string[]): boolean {
  if (words.length === 0) {
    return true;
  }
  const text = [
    task.taskId,
    task.name,
    task.description,
    task.dataset,
    task.bigQueryInitialPassesTableId,
    task.createdBy,
    task.predictionsConfiguration?.bigQueryPredictionTableId,
    ...(task.taskers ?? []),
    ...reviewersOf(task),
  ]
    .filter(value => value != null && value !== '')
    .join(' ')
    .toLowerCase();
  return words.every(word => text.includes(word));
}

export function filterTasks(
  tasks: AdminLabelTask[],
  query: TaskQuery
): AdminLabelTask[] {
  const words = query.search.toLowerCase().split(/\s+/).filter(Boolean);
  return tasks
    .filter(task => query.status === 'all' || taskStatus(task) === query.status)
    .filter(task => query.feature === 'any' || hasFeature(task, query.feature))
    .filter(task => !query.creator || task.createdBy === query.creator)
    .filter(task => !query.prefix || namePrefix(task) === query.prefix)
    .filter(
      task =>
        !query.person ||
        (task.taskers ?? []).includes(query.person) ||
        reviewersOf(task).includes(query.person)
    )
    .filter(task => matches(task, words))
    .sort(SORTS[query.sort].compare);
}

export type TaskGroup = { key: string; tasks: AdminLabelTask[] };

/**
 * The tasks in groups, each keeping the sort; groups by name, except months, newest first, and the
 * no-one groups last. One group with an empty key when there is no grouping.
 */
export function groupTasks(
  tasks: AdminLabelTask[],
  grouping: Grouping
): TaskGroup[] {
  const byKey = new Map<string, AdminLabelTask[]>();
  tasks.forEach(task =>
    GROUPINGS[grouping].keys(task).forEach(key => {
      const list = byKey.get(key);
      list ? list.push(task) : byKey.set(key, [task]);
    })
  );
  const groups = [...byKey.entries()].map(([key, list]) => ({
    key,
    tasks: list,
  }));
  const last = (key: string) =>
    key === NO_LABELLER || key === NO_REVIEWER ? 1 : 0;
  return grouping === 'month'
    ? groups.sort((a, b) => b.key.localeCompare(a.key))
    : groups.sort(
        (a, b) => last(a.key) - last(b.key) || a.key.localeCompare(b.key)
      );
}

export const PAGE_SIZES = [25, 50, 100, 200];
export const DEFAULT_PAGE_SIZE = 50;

export type PagedGroup = TaskGroup & {
  /** The group's rows on this page; empty for a folded group. */
  rows: AdminLabelTask[];
  /** The page starts inside the group: its first rows are on an earlier page. */
  continued: boolean;
};

/**
 * One page of the grouped rows, in the browser: groups run on from page to page, a folded group counts no
 * row and shows on the page where its rows would have started. `page` is zero-based; past the end it gives
 * the last page.
 */
export function paginate(
  groups: TaskGroup[],
  folded: Set<string>,
  page: number,
  size: number
): { groups: PagedGroup[]; page: number; pages: number; rows: number } {
  const rows = groups.reduce(
    (sum, group) => sum + (folded.has(group.key) ? 0 : group.tasks.length),
    0
  );
  const pages = Math.max(1, Math.ceil(rows / size));
  const current = Math.min(Math.max(0, page), pages - 1);
  const from = current * size;
  const to = from + size;
  const isLast = current === pages - 1;
  const paged: PagedGroup[] = [];
  let at = 0;
  groups.forEach(group => {
    const start = at;
    if (folded.has(group.key)) {
      if (
        (start >= from && start < to) ||
        (start >= to && isLast) ||
        (start === 0 && current === 0)
      ) {
        paged.push({ ...group, rows: [], continued: false });
      }
      return;
    }
    at += group.tasks.length;
    const a = Math.max(start, from);
    const b = Math.min(at, to);
    if (a < b) {
      paged.push({
        ...group,
        rows: group.tasks.slice(a - start, b - start),
        continued: a > start,
      });
    }
  });
  return { groups: paged, page: current, pages, rows };
}

/** The figures over the whole list, for the tiles. */
export function summarise(tasks: AdminLabelTask[], now = new Date()) {
  const since = new Date(now.getTime() - 30 * 24 * 3600 * 1000).toISOString();
  const count = (status: TaskStatus) =>
    tasks.filter(t => taskStatus(t) === status).length;
  return {
    total: tasks.length,
    active: count('active'),
    disabled: count('disabled'),
    predictionsPending: count('importing') + count('import-error'),
    predictionsFailed: count('import-error'),
    computing: count('computing'),
    itemsFailed: count('compute-error'),
    lastThirtyDays: tasks.filter(t => t.createdAt >= since).length,
  };
}

function csvCell(value: unknown): string {
  const text = value == null ? '' : String(value);
  return /[",\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** The tasks shown, as CSV: what the Notion table held, from what the platform knows. */
export function toCsv(tasks: AdminLabelTask[]): string {
  const header = [
    'taskId',
    'name',
    'status',
    'description',
    'dataset',
    'initialPassesTable',
    'predictionImportId',
    'predictionState',
    'predictionsTable',
    'computationState',
    'computationIssues',
    'labellers',
    'reviewers',
    'requiredLabelers',
    'revision',
    'createdBy',
    'createdAt',
  ];
  const rows = tasks.map(task => [
    task.taskId,
    task.name,
    statusLabel(taskStatus(task)),
    task.description,
    task.dataset,
    task.bigQueryInitialPassesTableId,
    task.predictionImportId,
    task.predictionState,
    task.predictionsConfiguration?.bigQueryPredictionTableId,
    task.computationState ?? 'READY',
    computationIssues(task),
    labellersOf(task).join(' '),
    reviewersOf(task).join(' '),
    task.requiredLabelers,
    task.revision,
    task.createdBy,
    task.createdAt,
  ]);
  return [header, ...rows].map(row => row.map(csvCell).join(',')).join('\n');
}
